import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { subMinutes } from 'date-fns';

import SessionNotification from '@medflex/components/stories/SessionNotification';
import useTokboxSession from '@medflex/hooks/useTokboxSession';
import Session from '@medflex/components/stories/Session/Session';
import useTokboxProxy from '@medflex/hooks/useTokboxProxy';
import config from '@medflex/utils/config';
import useSessionMeetings from '../hooks/useSessionMeetings';
import useUserProfile from '../hooks/useUserProfile';

const getUpcomingMeetings = meetings =>
  meetings.filter(meeting => Date.now() >= subMinutes(new Date(meeting.scheduledAt), 15).getTime());

const getJoinedParticipants = streams =>
  streams.map(({ connection }) => {
    const params = new URLSearchParams(connection.data);

    return params.get('participantId');
  });

const Notification = props => {
  const { id, scheduledAt, tokboxSession, participants, find, turnservers } = props;
  const [hidden, setHidden] = useState(true);
  const { register: registerTokboxSession, streams } = useTokboxSession();
  const { userProfile } = useUserProfile();
  const { participantId, tan } = participants.find(p => userProfile.id === p.userId);
  const joinedParticipants = useMemo(() => getJoinedParticipants(streams), [streams]);

  const onOpen = () => {
    window.open(`${config.SESSIONS_URL}/${id}/${participantId}?tan=${tan}`);
  };

  const onClose = () => {
    setHidden(true);
  };
  useEffect(() => {
    find(id, participantId, tan);
  }, []);

  useEffect(() => {
    setHidden(!joinedParticipants.length || joinedParticipants.includes(participantId));
  }, [joinedParticipants]);

  if (!tokboxSession?.token) {
    return null;
  }

  return (
    <div data-component="SessionNotificationModule" className="absolute right-0 top-0 z-10000">
      <Session
        apiKey={config.TOKBOX_API_KEY}
        id={tokboxSession.id}
        token={tokboxSession.token}
        autoConnect
        isProxyInitialized
        turnservers={turnservers}
        {...registerTokboxSession()}
      >
        <SessionNotification
          startDate={scheduledAt}
          onOpen={onOpen}
          onClose={onClose}
          hidden={hidden}
        />
      </Session>
    </div>
  );
};

Notification.propTypes = {
  id: PropTypes.string.isRequired,
  scheduledAt: PropTypes.string.isRequired,
  tokboxSession: PropTypes.shape({
    id: PropTypes.string.isRequired,
    token: PropTypes.string,
  }).isRequired,
  participants: PropTypes.arrayOf(PropTypes.shape).isRequired,
  find: PropTypes.func.isRequired,
  turnservers: PropTypes.arrayOf([
    PropTypes.exact({
      urls: PropTypes.arrayOf([PropTypes.string]),
      username: PropTypes.string,
      credential: PropTypes.string,
    }),
  ]).isRequired,
};

const SessionNotificationModule = () => {
  const { find, futureMeetings } = useSessionMeetings();
  const isProxyInitialized = useTokboxProxy({ url: config.TOKBOXY_PROXY_URL });
  const { userProfile } = useUserProfile();

  const upcomingMeetings = useMemo(() => {
    if (!futureMeetings?.length) {
      return [];
    }

    const participatingMeetings = futureMeetings.filter(meeting =>
      meeting.participants.some(p => p.userId === userProfile.id),
    );
    return getUpcomingMeetings(participatingMeetings);
  }, [futureMeetings, userProfile.id]);

  if (!upcomingMeetings.length) {
    return null;
  }

  return (
    isProxyInitialized &&
    upcomingMeetings.map(({ id, scheduledAt, tokboxSession, participants, turnCredentials }) => (
      <Notification
        key={id}
        id={id}
        scheduledAt={scheduledAt}
        tokboxSession={tokboxSession}
        participants={participants}
        find={find}
        turnservers={turnCredentials?.servers}
      />
    ))
  );
};

export default SessionNotificationModule;
